import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee, Trash2, Upload } from 'lucide-react';
import { useAuthStore } from '../../../store/authStore';
import { getSessionId } from '../../../services/api-client';
import {
    updateShopSettings, saveCustomCollectibles, getShopCollectibles,
    type RewardTier, type LoreOverride, type CollectibleItemPatch,
    type BirthdayPerk, type StampWindow, type StampMilestone,
} from '../../../services/shop-service';
import { syncRewardTiersOnChain, setCardSizeOnChain } from '../../../services/reward-sync';
import { validateMilestones } from './milestones';
import { LORE_DATABASE } from '../../dashboard/bit-brew-constants';
import { COLLECTIBLE_SPRITE_IDS, CollectibleSprite } from '../../dashboard/collectible-icons';
import { useShopSettingsLoad } from './useShopSettingsLoad';
import OwnerShell, { OwnerLoadingShell } from '../OwnerShell';
import CoralHeader from '../../dashboard/CoralHeader';

const COLLECTIBLE_RARITIES = ['common', 'uncommon', 'rare', 'legendary'] as const;
const MAX_CUSTOM_LOOT = 10;
const SAVE_CHIP_CLS = 'flex-none rounded-full bg-black/[.18] px-3.5 py-2 font-heading text-[8px] text-white disabled:opacity-50';

const DEFAULT_REWARDS: RewardTier[] = [
    { id: '1', name: 'Free Coffee', description: 'Any regular coffee', pointsCost: 100, rewardType: 'free_coffee' },
    { id: '2', name: '20% Discount', description: '20% off any purchase', pointsCost: 50, rewardType: 'discount_20' },
];

// Shared coral form styles.
const INPUT_CLS = 'w-full p-3 bg-background rounded-[8px] border-2 border-hairline focus:border-coral outline-none text-primary text-sm font-grotesk placeholder:text-body-grey';
const LABEL_CLS = 'block font-heading text-[8px] text-body-grey mb-1 uppercase';
const GHOST_PILL_CLS = 'rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[12px] px-3 py-2 transition-colors hover:border-coral hover:text-coral disabled:opacity-40 disabled:hover:border-hairline disabled:hover:text-body-grey';
const TRASH_BTN_CLS = 'p-2 text-error hover:text-error/70 shrink-0';

// Rarity accents, coral palette — same map LootBag.tsx (Task 8) uses for the
// customer-facing item-box grid: grey -> teal -> purple -> gold, ascending.
// Duplicated locally per this file set's established convention (INPUT_CLS/
// LABEL_CLS above are copied the same way) rather than exported/shared.
const RARITY_BORDER: Record<string, string> = {
    common: 'border-hairline', uncommon: 'border-pixel-cyan',
    rare: 'border-pixel-purple', legendary: 'border-accent-alt',
};
const RARITY_TEXT: Record<string, string> = {
    common: 'text-body-grey', uncommon: 'text-pixel-cyan',
    rare: 'text-pixel-purple', legendary: 'text-gold-ink',
};

/**
 * Second of four owner-settings sub-screens (Task 11 of the ShopSettings
 * split). Sections below are copied VERBATIM from ShopSettings.tsx (REWARD
 * TIERS, PERKS & HAPPY HOURS, CUSTOM LOOT, COFFEE LORE) per Nik's explicit
 * ruling — ShopSettings.tsx itself is untouched and stays routable until
 * Task 14 removes it. The four old per-section save buttons are collapsed
 * into one handleSaveAll, driven by the header's SAVE chip (Task 15 removed
 * the body's redundant full-width duplicate — same handler, same disabled
 * condition, nothing lost).
 */
function RewardsSettings() {
    const navigate = useNavigate();
    const { userAddress } = useAuthStore();
    const { loading, shopId, shopOnchainId, shopName, shopRow } = useShopSettingsLoad();

    const [rewards, setRewards] = useState<RewardTier[]>(DEFAULT_REWARDS);
    const [saving, setSaving] = useState(false);
    const [loreMode, setLoreMode] = useState<'default' | 'custom'>('default');
    const [loreOverrides, setLoreOverrides] = useState<Record<string, LoreOverride>>({});
    const [savingLore, setSavingLore] = useState(false);
    const [customLoot, setCustomLoot] = useState<CollectibleItemPatch[]>([]);
    const [savingLoot, setSavingLoot] = useState(false);
    const [uploadingLootIdx, setUploadingLootIdx] = useState<number | null>(null);
    // Engagement perks (041): birthday perk + happy hours + first-visit double.
    const [birthdayPerk, setBirthdayPerk] = useState<BirthdayPerk>({ enabled: false, title: '', bonus_stamps: 1 });
    const [stampWindows, setStampWindows] = useState<StampWindow[]>([]);
    const [firstVisitDouble, setFirstVisitDouble] = useState(true);
    const [savingPerks, setSavingPerks] = useState(false);
    const [savingAll, setSavingAll] = useState(false);
    // Card config (C1) + milestone ladder (C2).
    const [freeEveryN, setFreeEveryN] = useState(10);
    const [savedFreeEveryN, setSavedFreeEveryN] = useState(10);
    const [rewardLabel, setRewardLabel] = useState('');
    const [savingCard, setSavingCard] = useState(false);
    const [milestones, setMilestones] = useState<StampMilestone[]>([]);
    const [savingMilestones, setSavingMilestones] = useState(false);

    // Populate section state from the shared loader's row — mirrors the
    // relevant slice of ShopSettings.tsx's loadShopData(). getShopCollectibles
    // (custom loot) is a section-specific read the shared loader doesn't do —
    // replicated here, same as the original only fetching it once a DB row
    // (not just an on-chain capability) exists.
    useEffect(() => {
        if (!shopRow) return;
        if (Array.isArray(shopRow.reward_tiers) && shopRow.reward_tiers.length > 0) {
            setRewards(shopRow.reward_tiers);
        } else {
            const stored = localStorage.getItem(`shop_rewards_${shopRow.id}`);
            if (stored) setRewards(JSON.parse(stored));
        }
        setLoreMode(shopRow.lore_mode === 'custom' ? 'custom' : 'default');
        if (Array.isArray(shopRow.custom_lore)) {
            const map: Record<string, LoreOverride> = {};
            shopRow.custom_lore.forEach(o => { map[o.id] = o; });
            setLoreOverrides(map);
        }
        if (shopRow.birthday_perk) setBirthdayPerk(shopRow.birthday_perk);
        if (Array.isArray(shopRow.stamp_windows)) setStampWindows(shopRow.stamp_windows);
        setFirstVisitDouble(shopRow.first_visit_double !== false);
        const n = shopRow.free_every_n;
        setFreeEveryN(typeof n === 'number' && n >= 2 ? n : 10);
        setSavedFreeEveryN(typeof n === 'number' && n >= 2 ? n : 10);
        setRewardLabel(shopRow.reward_label || '');
        if (Array.isArray(shopRow.stamp_milestones)) setMilestones(shopRow.stamp_milestones);
        getShopCollectibles(shopRow.id).then(setCustomLoot);
    }, [shopRow]);

    // Capability-only path (no DB row yet) — same locally-stored-rewards
    // fallback ShopSettings.tsx's loadShopData() used before a shop ever
    // hits the backend.
    useEffect(() => {
        if (!shopId || shopRow) return;
        const stored = localStorage.getItem(`shop_rewards_${shopId}`);
        if (stored) setRewards(JSON.parse(stored));
    }, [shopId, shopRow]);

    function addReward() {
        const newId = Date.now().toString();
        setRewards([...rewards, {
            id: newId,
            name: '',
            description: '',
            pointsCost: 100,
            rewardType: `custom_${newId}`,
        }]);
    }

    function removeReward(id: string) {
        setRewards(rewards.filter(r => r.id !== id));
    }

    function updateReward(id: string, field: string, value: string | number) {
        setRewards(rewards.map(r =>
            r.id === id ? { ...r, [field]: value } : r
        ));
    }

    async function handleSaveCard() {
        if (!shopId) return;
        if (!Number.isInteger(freeEveryN) || freeEveryN < 2) {
            throw new Error('A card needs at least 2 coffees — the last one is the free one.');
        }
        setSavingCard(true);
        try {
            // Chain FIRST, exactly like handleSaveRewards. award_card_completion
            // divides by the on-chain value and never consults Postgres, so a
            // Postgres row written ahead of a failed transaction would draw a
            // card that fills at a rate the contract disagrees with.
            if (freeEveryN !== savedFreeEveryN) {
                const sync = await setCardSizeOnChain({ shopOnchainId, freeEveryN });
                if (!sync.ok) {
                    throw new Error(sync.reason === 'shop-not-onchain'
                        ? 'This shop has no on-chain record yet — card size can\'t be published.'
                        : 'On-chain update failed — nothing was changed. Retry in a minute.');
                }
            }
            const result = await updateShopSettings({
                shopId, free_every_n: freeEveryN, reward_label: rewardLabel.trim() || null,
            });
            if (!result) throw new Error('Save failed');
            setSavedFreeEveryN(freeEveryN);
        } finally {
            setSavingCard(false);
        }
    }

    function addMilestone() {
        setMilestones([...milestones, { at: 0, name: '' }]);
    }
    function removeMilestone(idx: number) {
        setMilestones(milestones.filter((_, i) => i !== idx));
    }
    function updateMilestone(idx: number, field: 'at' | 'name', value: string) {
        setMilestones(milestones.map((m, i) => i === idx
            ? { ...m, [field]: field === 'at' ? (parseInt(value, 10) || 0) : value }
            : m));
    }

    async function handleSaveMilestones() {
        if (!shopId) return;
        const { value, error } = validateMilestones(milestones);
        if (error) throw new Error(error);
        setSavingMilestones(true);
        try {
            const result = await updateShopSettings({ shopId, stamp_milestones: value });
            if (!result) throw new Error('Save failed');
            setMilestones(value);
        } finally {
            setSavingMilestones(false);
        }
    }

    async function handleSaveRewards() {
        if (!shopId) return;
        if (rewards.length === 0) {
            throw new Error('Add at least one reward — customers need something to redeem.');
        }
        const invalid = rewards.some(r =>
            !r.name.trim() || !Number.isInteger(r.pointsCost) || r.pointsCost < 1
        );
        if (invalid) {
            throw new Error('Every reward needs a name and a cost of at least 1 bean.');
        }
        setSaving(true);
        try {
            const sync = await syncRewardTiersOnChain({ shopOnchainId, ownerAddress: userAddress!, tiers: rewards });
            if (!sync.ok) {
                localStorage.setItem(`shop_rewards_${shopId}`, JSON.stringify(rewards));
                throw new Error(sync.reason === 'shop-not-onchain'
                    ? 'This shop has no on-chain record yet — rewards can\'t be published. Contact support.'
                    : sync.reason === 'no-capability'
                        ? 'Your shop capability wasn\'t found in this wallet — sign out/in and retry.'
                        : 'On-chain update failed — saved locally; retry in a minute.');
            }
            const result = await updateShopSettings({ shopId, reward_tiers: rewards });
            if (!result) throw new Error('Save failed');
            localStorage.removeItem(`shop_rewards_${shopId}`);
        } catch (err) {
            console.error('Failed to save rewards:', err);
            localStorage.setItem(`shop_rewards_${shopId}`, JSON.stringify(rewards));
            throw err;
        } finally {
            setSaving(false);
        }
    }

    function updateLoreOverride(id: string, field: 'title' | 'text', value: string) {
        setLoreOverrides(prev => {
            const base = LORE_DATABASE.find(l => l.id === id)!;
            const current = prev[id] || { id, title: base.title, text: base.text };
            return { ...prev, [id]: { ...current, [field]: value } };
        });
    }

    async function handleSaveLore() {
        if (!shopId) return;
        setSavingLore(true);
        try {
            // Only persist entries that actually differ from the defaults.
            const overrides = Object.values(loreOverrides).filter(o => {
                const base = LORE_DATABASE.find(l => l.id === o.id);
                return base && (o.title !== base.title || o.text !== base.text) && o.title.trim() && o.text.trim();
            });
            const result = await updateShopSettings({
                shopId,
                lore_mode: loreMode,
                custom_lore: overrides,
            });
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save lore:', err);
            throw err;
        } finally {
            setSavingLore(false);
        }
    }

    async function handleSavePerks() {
        if (!shopId) return;
        if (birthdayPerk.enabled && !birthdayPerk.title.trim()) {
            throw new Error('Give the birthday perk a name customers and staff will see (e.g. "Free birthday coffee").');
        }
        for (const w of stampWindows) {
            if (w.start >= w.end) {
                throw new Error('Each happy hour must end after it starts (and cannot cross midnight).');
            }
        }
        setSavingPerks(true);
        try {
            const result = await updateShopSettings({
                shopId,
                birthday_perk: birthdayPerk.enabled || birthdayPerk.title.trim()
                    ? { ...birthdayPerk, title: birthdayPerk.title.trim() }
                    : null,
                stamp_windows: stampWindows.length > 0 ? stampWindows.map(w => ({ ...w, multiplier: 2 })) : null,
                first_visit_double: firstVisitDouble,
            });
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save perks:', err);
            throw err;
        } finally {
            setSavingPerks(false);
        }
    }

    function addLootItem() {
        if (customLoot.length >= MAX_CUSTOM_LOOT) return;
        setCustomLoot([...customLoot, {
            name: '', description: '', rarity: 'common', icon: COLLECTIBLE_SPRITE_IDS[0],
        }]);
    }

    function removeLootItem(index: number) {
        setCustomLoot(customLoot.filter((_, i) => i !== index));
    }

    function updateLootItem(index: number, field: keyof CollectibleItemPatch, value: string) {
        setCustomLoot(customLoot.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        ));
    }

    // Direct per-item upload, triggered on file select rather than through the
    // aggregated SAVE REWARDS flow — keeps its own alert-and-swallow instead
    // of throwing, since nothing downstream would catch it.
    async function handleUploadLootArt(index: number, file: File) {
        if (!userAddress) return;
        setUploadingLootIdx(index);
        try {
            const formPayload = new FormData();
            formPayload.append('logo', file);
            formPayload.append('owner', userAddress);
            formPayload.append('kind', 'collectible');

            const sessionId = getSessionId();
            const uploadRes = await fetch('/api/shops/upload-logo', {
                method: 'POST',
                headers: sessionId ? { Authorization: `Bearer ${sessionId}` } : undefined,
                body: formPayload,
            });
            if (!uploadRes.ok) {
                const body = await uploadRes.json().catch(() => null);
                throw new Error(body?.error || `Upload failed (${uploadRes.status})`);
            }
            const uploadData = await uploadRes.json();
            updateLootItem(index, 'icon', uploadData.pixelUrl || uploadData.url);
        } catch (err) {
            console.error('Failed to upload collectible art:', err);
            alert('Could not upload art — try again.');
        } finally {
            setUploadingLootIdx(null);
        }
    }

    async function handleSaveLoot() {
        if (!shopId) return;
        setSavingLoot(true);
        try {
            const result = await saveCustomCollectibles(shopId, customLoot);
            if (!result) throw new Error('Save failed');
        } catch (err) {
            console.error('Failed to save custom loot:', err);
            throw err;
        } finally {
            setSavingLoot(false);
        }
    }

    // ONE combined save for the whole REWARDS group — sequentially awaits each
    // section's existing save logic (unconditionally, same as the old page's
    // four separate save buttons — saveCustomCollectibles is a full-replace
    // endpoint, so gating it on customLoot.length > 0 would silently prevent
    // "delete all loot" from ever persisting) and reports which section(s)
    // failed rather than bailing on the first error.
    //
    // Card and milestones are in here too (2026-08-12). They shipped with only
    // their own buttons, so a header chip labelled SAVE on a six-section page
    // silently discarded two of them — an owner who filled in a milestone
    // ladder and hit SAVE was told "Rewards saved!" and lost it. Card goes
    // first because it is the only section that can prompt a signature.
    async function handleSaveAll() {
        if (!shopId) return;
        setSavingAll(true);
        const failed: string[] = [];
        try { await handleSaveCard(); } catch (err) { failed.push(err instanceof Error ? err.message : 'stamp card'); }
        try { await handleSaveMilestones(); } catch (err) { failed.push(err instanceof Error ? err.message : 'milestones'); }
        try { await handleSaveRewards(); } catch (err) { failed.push(err instanceof Error ? err.message : 'reward tiers'); }
        try { await handleSaveLoot(); } catch { failed.push('custom loot'); }
        try { await handleSaveLore(); } catch { failed.push('lore'); }
        try { await handleSavePerks(); } catch (err) { failed.push(err instanceof Error ? err.message : 'perks'); }
        setSavingAll(false);
        if (failed.length > 0) {
            alert(`Some reward sections failed to save: ${failed.join(', ')}. Try again.`);
        } else {
            alert('Rewards saved!');
        }
    }

    if (loading) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="Loading…">
                <div className="space-y-3.5">
                    <div className="bg-white rounded-[10px] h-24 animate-pulse" />
                    <div className="bg-white rounded-[10px] h-64 animate-pulse" />
                </div>
            </OwnerLoadingShell>
        );
    }

    if (!shopId) {
        return (
            <OwnerLoadingShell active="setup" statusLabel="SETUP" title="No shop yet">
                <div className="bg-white rounded-[10px] text-center py-12">
                    <Coffee size={40} className="mx-auto mb-4 text-coral" />
                    <p className="font-heading text-[10px] text-body-grey">NO SHOP REGISTERED YET</p>
                </div>
            </OwnerLoadingShell>
        );
    }

    return (
        <OwnerShell
            active="setup"
            header={
                <CoralHeader
                    statusLabel="SETUP"
                    eyebrow="REWARDS SETTINGS"
                    title={shopName}
                    chip={
                        <button
                            onClick={handleSaveAll}
                            disabled={savingAll || saving || savingLoot || savingLore || savingPerks || savingCard || savingMilestones}
                            className={SAVE_CHIP_CLS}
                        >
                            {savingAll ? 'SAVING…' : 'SAVE'}
                        </button>
                    }
                />
            }
        >
        <div className="space-y-3.5 pb-8">
            {/* sr-only: CoralHeader's title already carries the shop name —
                mockup drops the repeated in-body page title. */}
            <h1 className="sr-only">Rewards</h1>

            <button
                onClick={() => navigate('/shops/settings')}
                className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
            >
                <ArrowLeft size={13} /> Settings
            </button>


            {/* Stamp card (C1) — the UI set_card_size has been waiting for
                since the mainnet v4 publish. */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Stamp card</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    How many coffees fill a card, and what the free one is.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <div>
                        {/* "Coffees per free one" read as "how many they buy
                            to earn one" (9), but the number is the whole card
                            INCLUDING the free coffee (10) — Nik hit exactly
                            that ambiguity. The label now names the card, and
                            the line under it states the deal in the words a
                            barista would use. */}
                        <label className={LABEL_CLS} htmlFor="free-every-n">Coffees on a card</label>
                        <input
                            id="free-every-n"
                            type="number" min={2} max={50} value={freeEveryN}
                            onChange={(e) => setFreeEveryN(parseInt(e.target.value, 10) || 0)}
                            className={INPUT_CLS}
                        />
                        <p className="font-grotesk font-medium text-[12px] text-body-grey mt-1">
                            Buy {Math.max(freeEveryN - 1, 0)}, get the {freeEveryN}th free. Count the free
                            coffee in this number.
                        </p>
                    </div>
                    <div>
                        <label className={LABEL_CLS} htmlFor="reward-label">What they get</label>
                        <input
                            id="reward-label"
                            type="text" maxLength={40} value={rewardLabel}
                            onChange={(e) => setRewardLabel(e.target.value)}
                            placeholder="Free coffee"
                            className={INPUT_CLS}
                        />
                    </div>
                </div>
                {freeEveryN !== savedFreeEveryN && (
                    <p className="font-grotesk font-medium text-[13px] text-error mt-2">
                        Changing card size does not re-fit cards already in progress.
                        On-chain purchases are a running count and the card is derived
                        by division, so raising this moves existing customers further
                        from their next free coffee.
                    </p>
                )}
                <button
                    onClick={() => { handleSaveCard().catch((e) => alert(e.message)); }}
                    disabled={savingCard}
                    className="mt-3.5 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
                >
                    {savingCard ? 'Saving…' : 'Save card'}
                </button>
            </div>

            {/* Milestone rewards (C2) */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between mb-3.5 flex-wrap gap-3">
                    <h2 className="font-grotesk font-bold text-[17px] text-primary">Milestone rewards</h2>
                    <button onClick={addMilestone} className="rounded-full bg-tab-inactive text-body-grey font-grotesk font-bold text-[13px] px-3.5 py-2 hover:bg-hairline">
                        + Add
                    </button>
                </div>
                <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3.5">
                    One-off rewards for lifetime coffees at your shop. The stamp card
                    keeps running underneath — these sit on top of it. Staff approve
                    each one at the counter, the same as a free coffee.
                </p>
                <div className="space-y-3">
                    {milestones.map((m, idx) => (
                        <div key={idx} className="p-3 rounded-[8px] bg-background border-2 border-hairline">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                                <div>
                                    <label className={LABEL_CLS}>At how many coffees</label>
                                    <input
                                        type="number" min={1} value={m.at || ''}
                                        onChange={(e) => updateMilestone(idx, 'at', e.target.value)}
                                        placeholder="100"
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div>
                                    <label className={LABEL_CLS}>What they get</label>
                                    <input
                                        type="text" maxLength={60} value={m.name}
                                        onChange={(e) => updateMilestone(idx, 'name', e.target.value)}
                                        placeholder="Free bag of beans"
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button onClick={() => removeMilestone(idx)} className={TRASH_BTN_CLS} title="Remove milestone">
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    {milestones.length === 0 && (
                        <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-4">
                            No milestones yet — add one to reward your regulars.
                        </p>
                    )}
                </div>
                <button
                    onClick={() => { handleSaveMilestones().catch((e) => alert(e.message)); }}
                    disabled={savingMilestones}
                    className="mt-3.5 rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6 disabled:opacity-50"
                >
                    {savingMilestones ? 'Saving…' : 'Save milestones'}
                </button>
            </div>

            {/* Reward Tiers — mockup #8c */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between mb-3.5 flex-wrap gap-3">
                    <h2 className="font-grotesk font-bold text-[17px] text-primary">Reward tiers</h2>
                    <button onClick={addReward} className="rounded-full bg-tab-inactive text-body-grey font-grotesk font-bold text-[13px] px-3.5 py-2 hover:bg-hairline">
                        + Add
                    </button>
                </div>
                <p className="font-grotesk font-medium text-[13px] text-body-grey mb-3.5">
                    What customers can spend their beans on. 10 beans per coffee — price rewards to fit your margins.
                </p>

                <div className="space-y-3">
                    {rewards.map((reward) => (
                        <div key={reward.id} className="p-3 rounded-[8px] bg-background border-2 border-hairline">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5">
                                <div>
                                    <label className={LABEL_CLS}>Name</label>
                                    <input
                                        type="text"
                                        value={reward.name}
                                        onChange={(e) => updateReward(reward.id, 'name', e.target.value)}
                                        placeholder="Free Coffee"
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div>
                                    <label className={LABEL_CLS}>Description</label>
                                    <input
                                        type="text"
                                        value={reward.description}
                                        onChange={(e) => updateReward(reward.id, 'description', e.target.value)}
                                        placeholder="Any regular coffee"
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div>
                                    <label className={LABEL_CLS}>Bean cost</label>
                                    <input
                                        type="number"
                                        value={reward.pointsCost}
                                        onChange={(e) => updateReward(reward.id, 'pointsCost', parseInt(e.target.value) || 0)}
                                        min={10}
                                        step={10}
                                        className={INPUT_CLS}
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button
                                        onClick={() => removeReward(reward.id)}
                                        className={TRASH_BTN_CLS}
                                        title="Remove reward"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {rewards.length === 0 && (
                        <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-4">
                            No rewards configured yet. Add one above!
                        </p>
                    )}
                </div>
            </div>

            {/* Perks & happy hours (041) */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Perks &amp; happy hours</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Automatic reasons to come back: a birthday treat, double-stamp
                    happy hours, and a first-visit welcome bonus.
                </p>

                <label className="flex items-center gap-3 mb-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={birthdayPerk.enabled}
                        onChange={(e) => setBirthdayPerk({ ...birthdayPerk, enabled: e.target.checked })}
                        className="w-4 h-4 accent-coral"
                    />
                    <span className="font-grotesk font-bold text-[13px] text-primary">🎂 Birthday perk</span>
                </label>
                {birthdayPerk.enabled && (
                    <div className="ml-7 mb-4 space-y-2">
                        <div>
                            <label className={LABEL_CLS}>What they get (staff see this at the counter)</label>
                            <input
                                type="text" maxLength={60} value={birthdayPerk.title}
                                onChange={(e) => setBirthdayPerk({ ...birthdayPerk, title: e.target.value })}
                                placeholder="Free birthday coffee on us"
                                className={INPUT_CLS}
                            />
                        </div>
                        <div>
                            <label className={LABEL_CLS}>Auto bonus stamps (once a year, during their birthday week)</label>
                            <select
                                value={birthdayPerk.bonus_stamps}
                                onChange={(e) => setBirthdayPerk({ ...birthdayPerk, bonus_stamps: Number(e.target.value) })}
                                className={INPUT_CLS}
                            >
                                <option value={0}>None — perk only</option>
                                <option value={1}>+1 stamp</option>
                                <option value={2}>+2 stamps</option>
                            </select>
                        </div>
                    </div>
                )}

                <label className="flex items-center gap-3 mb-2 mt-4 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={firstVisitDouble}
                        onChange={(e) => setFirstVisitDouble(e.target.checked)}
                        className="w-4 h-4 accent-coral"
                    />
                    <span className="font-grotesk font-bold text-[13px] text-primary">⭐ First-visit double — new members' first stamp counts twice</span>
                </label>

                <div className="flex items-center justify-between mt-5 mb-2 flex-wrap gap-2">
                    <span className="font-grotesk font-bold text-[13px] text-primary">⚡ Double-stamp happy hours</span>
                    <button
                        onClick={() => stampWindows.length < 8 && setStampWindows([...stampWindows, { day: 3, start: '14:00', end: '16:00', multiplier: 2 }])}
                        disabled={stampWindows.length >= 8}
                        className={GHOST_PILL_CLS}
                    >
                        + Add window
                    </button>
                </div>
                <p className="font-grotesk font-medium text-[12px] text-body-grey mb-3">
                    Purchases in these windows earn 2× stamps automatically (shop's local time).
                </p>
                <div className="space-y-2">
                    {stampWindows.map((w, idx) => (
                        <div key={idx} className="p-3 rounded-[8px] bg-background border-2 border-hairline grid grid-cols-2 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
                            <div>
                                <label className={LABEL_CLS}>Day</label>
                                <select
                                    value={w.day}
                                    onChange={(e) => setStampWindows(stampWindows.map((x, i) => i === idx ? { ...x, day: Number(e.target.value) } : x))}
                                    className={INPUT_CLS}
                                >
                                    {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                                        <option key={d} value={i}>{d}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={LABEL_CLS}>From</label>
                                <input
                                    type="time" value={w.start}
                                    onChange={(e) => setStampWindows(stampWindows.map((x, i) => i === idx ? { ...x, start: e.target.value } : x))}
                                    className={INPUT_CLS}
                                />
                            </div>
                            <div>
                                <label className={LABEL_CLS}>To</label>
                                <input
                                    type="time" value={w.end}
                                    onChange={(e) => setStampWindows(stampWindows.map((x, i) => i === idx ? { ...x, end: e.target.value } : x))}
                                    className={INPUT_CLS}
                                />
                            </div>
                            <button
                                onClick={() => setStampWindows(stampWindows.filter((_, i) => i !== idx))}
                                className={TRASH_BTN_CLS}
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Custom Loot — mockup #9d */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <div className="flex items-center justify-between mb-[5px] flex-wrap gap-3">
                    <h2 className="font-grotesk font-bold text-[17px] text-primary">Custom loot</h2>
                    <button
                        onClick={addLootItem}
                        disabled={customLoot.length >= MAX_CUSTOM_LOOT}
                        className={GHOST_PILL_CLS}
                    >
                        + Add item
                    </button>
                </div>
                <p className="font-heading text-[8px] text-body-grey uppercase mb-2 tracking-[1px]">
                    {customLoot.length} of {MAX_CUSTOM_LOOT} slots used
                </p>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Custom loot drops for YOUR shop — customers can win these alongside the default set when they redeem a free coffee.
                </p>

                <div className="space-y-3">
                    {customLoot.map((item, index) => (
                        <div key={index} className="rounded-[10px] border-2 border-hairline p-4">
                            <div className="flex items-center gap-3.5 mb-3.5">
                                <div className={`flex-none w-14 h-14 rounded-[10px] border-[3px] bg-white flex items-center justify-center ${RARITY_BORDER[item.rarity] || RARITY_BORDER.common}`}>
                                    <CollectibleSprite icon={item.icon} className={`w-9 h-9 ${RARITY_TEXT[item.rarity] || RARITY_TEXT.common}`} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <input
                                        type="text"
                                        maxLength={60}
                                        value={item.name}
                                        onChange={(e) => updateLootItem(index, 'name', e.target.value)}
                                        placeholder="House Mug"
                                        className="w-full font-grotesk font-bold text-[15px] text-primary outline-none border-b-2 border-transparent focus:border-coral bg-transparent"
                                    />
                                    <span className={`font-heading text-[8px] uppercase ${RARITY_TEXT[item.rarity] || RARITY_TEXT.common}`}>{item.rarity}</span>
                                </div>
                                <button
                                    onClick={() => removeLootItem(index)}
                                    className={TRASH_BTN_CLS}
                                    title="Remove item"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>

                            <label className={LABEL_CLS}>Description</label>
                            <input
                                type="text"
                                maxLength={200}
                                value={item.description}
                                onChange={(e) => updateLootItem(index, 'description', e.target.value)}
                                placeholder="Our house mug, pixelated."
                                className={`${INPUT_CLS} mb-3`}
                            />

                            {/* Rarity chips — same field as the old RARITY select
                                (updateLootItem(index,'rarity',r)), reskinned as
                                mockup #9d's tappable rarity row instead of a dropdown. */}
                            <label className={LABEL_CLS}>Rarity</label>
                            <div className="flex gap-2 flex-wrap mb-3">
                                {COLLECTIBLE_RARITIES.map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => updateLootItem(index, 'rarity', r)}
                                        className={`rounded-full font-grotesk font-bold text-[11px] px-3 py-[7px] capitalize ${
                                            item.rarity === r ? 'bg-coral text-white' : 'bg-tab-inactive text-body-grey'
                                        }`}
                                    >
                                        {r}
                                    </button>
                                ))}
                            </div>

                            {/* Sprite picker — per item, per the brief ("sprite picker
                                per item"); small square tiles, not a text dropdown. */}
                            <label className={LABEL_CLS}>Sprite</label>
                            <div className="flex gap-1.5 flex-wrap mb-3">
                                {COLLECTIBLE_SPRITE_IDS.map((id) => {
                                    const selected = item.icon === id;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            aria-label={id}
                                            onClick={() => updateLootItem(index, 'icon', id)}
                                            className={`w-8 h-8 rounded-[6px] border-2 flex items-center justify-center shrink-0 ${
                                                selected ? 'border-coral bg-coral/10' : 'border-hairline bg-background'
                                            }`}
                                        >
                                            <CollectibleSprite icon={id} className={`w-5 h-5 ${selected ? 'text-coral' : 'text-body-grey'}`} />
                                        </button>
                                    );
                                })}
                            </div>

                            <label className={`${GHOST_PILL_CLS} inline-flex items-center gap-2 cursor-pointer`}>
                                <Upload size={12} />
                                {uploadingLootIdx === index ? 'Uploading…' : 'Upload art'}
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="hidden"
                                    disabled={uploadingLootIdx !== null}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUploadLootArt(index, file);
                                        e.target.value = '';
                                    }}
                                />
                            </label>
                        </div>
                    ))}

                    {customLoot.length === 0 && (
                        <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-4">
                            No custom loot yet. Add an item above!
                        </p>
                    )}

                    {/* Dashed add card — mockup #9d */}
                    <button
                        type="button"
                        onClick={addLootItem}
                        disabled={customLoot.length >= MAX_CUSTOM_LOOT}
                        className="w-full rounded-[10px] border-[3px] border-dashed border-hairline p-5 text-center disabled:opacity-40 hover:border-coral"
                    >
                        <p className="font-grotesk font-bold text-[15px] text-body-grey mb-1.5">+ Add loot item</p>
                        <p className="font-grotesk font-medium text-xs text-body-grey">Pick a sprite or upload your own art</p>
                    </button>
                </div>
            </div>

            {/* Coffee Lore */}
            <div className="bg-white rounded-[10px] p-[18px]">
                <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Coffee lore</h2>
                <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey mb-3.5">
                    Customers unlock short lore cards as they earn stamps. Use the built-in
                    Bit Brew set, or tell your own shop's stories — same unlock milestones,
                    your words.
                </p>

                {/* Segmented pill — class-mapping table */}
                <div className="flex rounded-full bg-tab-inactive p-[3px] mb-3.5 w-fit">
                    <button
                        onClick={() => setLoreMode('default')}
                        className={`rounded-full font-grotesk font-bold text-[12px] px-3.5 py-2 ${
                            loreMode === 'default' ? 'bg-coral text-white' : 'text-body-grey'
                        }`}
                    >
                        Default
                    </button>
                    <button
                        onClick={() => setLoreMode('custom')}
                        className={`rounded-full font-grotesk font-bold text-[12px] px-3.5 py-2 ${
                            loreMode === 'custom' ? 'bg-coral text-white' : 'text-body-grey'
                        }`}
                    >
                        Custom
                    </button>
                </div>

                {loreMode === 'custom' && (
                    <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
                        {LORE_DATABASE.map((entry) => {
                            const o = loreOverrides[entry.id];
                            return (
                                <div key={entry.id} className="p-3 rounded-[8px] bg-background border-2 border-hairline">
                                    <p className="font-heading text-[8px] text-body-grey mb-2">
                                        UNLOCKS: {entry.condition.toUpperCase()}
                                    </p>
                                    <input
                                        type="text"
                                        maxLength={60}
                                        value={o?.title ?? entry.title}
                                        onChange={(e) => updateLoreOverride(entry.id, 'title', e.target.value)}
                                        className={`${INPUT_CLS} mb-2 font-bold text-coral`}
                                    />
                                    <textarea
                                        maxLength={500}
                                        rows={2}
                                        value={o?.text ?? entry.text}
                                        onChange={(e) => updateLoreOverride(entry.id, 'text', e.target.value)}
                                        className={INPUT_CLS}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
        </OwnerShell>
    );
}

export default RewardsSettings;
